<template>
  <div
    v-if="!hardGate20516Active"
    ref="navbar"
    class="navbar-mobile"
    :class="{ 'navbar-mobile--ios': view?.isIOS }"
  >
    <AppMenu :view="view" />
  </div>
</template>

<script setup lang="ts">
import {
  onActivated,
  onBeforeMount,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
} from 'vue'
import type { Raw } from 'vue'

import { HeaderView } from '@/models/header'
import { useHardGateBilling20516Active } from '@/app/composables/useHardGateBilling20516'

import AppMenu from '@/app/components/header/AppMenu.vue'

const view = ref<Raw<HeaderView>>(new HeaderView())
const hardGate20516Active = useHardGateBilling20516Active()

onBeforeMount(async () => {
  await view.value.init()
})

// Publish the navbar's height so fixed elements (e.g. bottom toasts)
// can sit above it. The height is 0 when the navbar is hidden on desktop.
const NAVBAR_HEIGHT_PROPERTY = '--navbar-mobile-height'
const navbar = ref<HTMLElement | null>(null)
let resizeObserver: ResizeObserver | null = null

function updateNavbarHeight(): void {
  document.documentElement.style.setProperty(
    NAVBAR_HEIGHT_PROPERTY,
    `${navbar.value?.offsetHeight ?? 0}px`,
  )
}

function startPublishingHeight(): void {
  // On the first mount of a cached page, `onMounted` and `onActivated` both
  // run. So before we create the observer, stop publishing so that there will
  // only be one observer active at a time.
  stopPublishingHeight()
  updateNavbarHeight()
  if (typeof ResizeObserver !== 'undefined' && navbar.value) {
    resizeObserver = new ResizeObserver(updateNavbarHeight)
    resizeObserver.observe(navbar.value)
  }
}

function stopPublishingHeight(): void {
  resizeObserver?.disconnect()
  resizeObserver = null
  document.documentElement.style.removeProperty(NAVBAR_HEIGHT_PROPERTY)
}

// All pages have onMounted and onBeforeUnmount, but for pages cached with
// `KeepAlive` (e.g. Books browse), they are
// - activated but not mounted on renders after the first one
// - deactivated rather than unmounted when you leave them
// So we add onActivated and onDeactivated here.
onMounted(startPublishingHeight)
onActivated(startPublishingHeight)
onDeactivated(stopPublishingHeight)
onBeforeUnmount(stopPublishingHeight)
</script>

<style lang="scss" scoped>
.navbar-mobile {
  position: sticky;
  z-index: 5;
  left: 0;
  bottom: 0;
  background-color: $c-white;
  border-top: 1px solid $c-border;
  padding: 6px 28px 13px 6px;

  @media only screen and (max-width: $w-medium-s) {
    padding: 6px 13px 13px 13px;
  }

  @media only screen and (min-width: $w-tablet-b) {
    display: none;
  }
}

.navbar-mobile--ios {
  @supports (padding-bottom: env(safe-area-inset-top)) {
    padding-bottom: env(safe-area-inset-bottom);
  }
}

.night-mode .navbar-mobile {
  background-color: $c-dark-2;
  border-color: $c-dark-5;
}
</style>
